import { useMemo, useState } from 'react'
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import type { WorkerTier } from '../tier.js'

export type SortDirection = 'asc' | 'desc'

/** What a column sorts on. Null means "nothing recorded", and always sorts last. */
export type SortValue = string | number | null

export type SortAccessors<T, K extends string> = Readonly<Record<K, (row: T) => SortValue>>

/** Tiers in the order they rank, so ascending puts the best tier first rather than sorting the names alphabetically. */
export const TIER_SORT_RANK: Readonly<Record<WorkerTier, number>> = Object.freeze({ gold: 0, silver: 1, bronze: 2, probationary: 3 })

function compare(a: SortValue, b: SortValue, direction: SortDirection): number {
  if (a === null || b === null) {
    // Missing values go to the bottom whichever way the column is sorted.
    return a === b ? 0 : a === null ? 1 : -1
  }
  const order = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' })
  return direction === 'asc' ? order : -order
}

/**
 * Sorting for a list shown on screen. A column header cycles ascending,
 * descending, then back to the order the list arrived in. Rows that compare
 * equal keep that original order, so a sort never shuffles ties.
 *
 * `accessors` should be a constant defined outside the component: one
 * function per sortable column, returning what that column sorts on.
 */
export function useSort<T, K extends string>(rows: readonly T[], accessors: SortAccessors<T, K>) {
  const [sort, setSort] = useState<{ key: K; direction: SortDirection } | null>(null)

  const sorted = useMemo(() => {
    if (!sort) {
      return [...rows]
    }
    const valueOf = accessors[sort.key]
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => compare(valueOf(a.row), valueOf(b.row), sort.direction) || a.index - b.index)
      .map(({ row }) => row)
  }, [rows, sort, accessors])

  /** The props a header for `key` needs: which way it is sorted, if at all, and what a click does. */
  function header(key: K): { direction: SortDirection | null; onSort: () => void } {
    return {
      direction: sort?.key === key ? sort.direction : null,
      onSort: () => setSort((current) => (current?.key !== key ? { key, direction: 'asc' } : current.direction === 'asc' ? { key, direction: 'desc' } : null)),
    }
  }

  return { sorted, header }
}

interface SortButtonProps {
  label: string
  direction: SortDirection | null
  onSort: () => void
  /** `dark` for a header on a dark bar. */
  tone?: 'light' | 'dark'
  alignRight?: boolean
}

const NEXT_STEP: Record<'none' | SortDirection, string> = { none: 'sort ascending', asc: 'sort descending', desc: 'clear sorting' }

/** A column heading that sorts when clicked, with an arrow showing the current direction (or that it can be sorted). */
export function SortButton({ label, direction, onSort, tone = 'light', alignRight = false }: SortButtonProps) {
  const Icon = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ArrowUpDown
  const colors =
    tone === 'dark'
      ? direction
        ? 'text-white'
        : 'text-white/60 hover:text-white'
      : direction
        ? 'text-marine'
        : 'text-ink-mid hover:text-ink'
  return (
    <button
      type="button"
      onClick={onSort}
      title={`${label}: ${NEXT_STEP[direction ?? 'none']}`}
      className={`group inline-flex items-center gap-1 font-bold uppercase tracking-wide transition-colors focus:outline-none focus-visible:underline ${colors} ${alignRight ? 'flex-row-reverse' : ''}`}
    >
      {label}
      <Icon aria-hidden="true" strokeWidth={2.25} className={`h-3 w-3 shrink-0 ${direction ? '' : 'opacity-50 group-hover:opacity-100'}`} />
    </button>
  )
}

interface SortableThProps extends Omit<SortButtonProps, 'tone'> {
  className?: string
}

/** A table header cell that sorts its column. Announces the sort state to assistive technology. */
export function SortableTh({ className = 'px-4 py-2', alignRight = false, ...button }: SortableThProps) {
  return (
    <th className={`${className} ${alignRight ? 'text-right' : ''}`} aria-sort={button.direction === 'asc' ? 'ascending' : button.direction === 'desc' ? 'descending' : 'none'}>
      <SortButton {...button} alignRight={alignRight} />
    </th>
  )
}
